import React from 'react'
import { useAuthHeaders } from '@hooks/useAuthHeaders'
import { useAuthentication } from '@auth/hooks/useAuthentication'

import { ISearchLocation, SaveSearchPayload } from '@typescript'
import { AvailabilityActionType, TMkAvailableSuite, AvailabilityAction, AvailabilityProviderProps, AvailabilityState } from './types'
import { MkSearchResponse, MkFilterCategory, MkResortAvailRequest, MkResortAvailResponse } from './services/searchTypes'
import { useSiteContext } from '@contexts/MkSiteContext'
import { fetchSearchConstraints } from './services/MkApiSearch'
import { mapSearchConstraintsResponse, getDefaultSearchConstraints } from './utils/searchConstraintsUtils'

import { initializeAvailabilityState } from './state'
import { availabilityReducer } from './reducer'
import AvailabilityService from './service'
import { MAX_STAY_MONTHS, MAX_STAY_MONTHS_EBAC } from '@shared/constants'

export interface AvailabilityContextProps extends AvailabilityState {
  dispatchAvailabilityAction: React.Dispatch<AvailabilityAction>
  onDateChange: (date: Date | (Date | null)[]) => void
  onMonthChange: (date: Date | (Date | null)[]) => void
  resetDates: () => void
  resetLocation: () => void
  resetFlexibleDates: () => void
  resetBonusTimeDates: () => void
  registerBonusTimeState: (isBonusTimeEnabled: boolean, isExoticBonusTimeEnabled: boolean) => void
  onLocationChange: (location: ISearchLocation) => void
  onFlexibleDatesChange: (isFlexibleDates: boolean) => void
  onBonusTimeDatesChange: (isBonusTimeDates: boolean) => void
  setDatesBufferEnabled: (isDatesBufferEnabled: boolean) => void
  setSelectedFlexibleDateMonths: (selectedMonths: Date[]) => void
  setNumberOfNights: (numberOfNights: number) => void
  formattedDates: string
  setFlexiDatesString: (flexiDatesString: string) => void
  setBonusTimeDatesString: (bonusTimeDatesString: string) => void
  setAvailableSuites: (suites: TMkAvailableSuite[]) => void
  setSelectedSuite: (suiteId: string | null) => void
  setSelectedResort: (resortKey: string | null) => void
  setSavedSearch: (savedSearch: SaveSearchPayload) => void
  setLoadingState: (isLoading: boolean) => void
  getFormattedDates: () => string
  fetchSavedSearches: (userName: string, brand: string, accessCode: string, jwtToken: string) => Promise<any>
  createSaveSearchPayload: (searchName?: string) => SaveSearchPayload
  storeSavedSearch: (userName: string, brand: string, accessCode: string, jwtToken: string, payload?: SaveSearchPayload) => Promise<any>
  deleteSavedSearch: (userName: string, brand: string, searchId: string, accessCode: string, jwtToken: string) => Promise<boolean>
  searchResorts: (options?: {
    pageNum?: number
    pageSize?: number
    filterList?: MkFilterCategory[]
    sortBy?: string
    latitude?: string
    longitude?: string
  }) => Promise<MkSearchResponse>
  searchResortAvail: (request: MkResortAvailRequest) => Promise<MkResortAvailResponse>
  setFilters: (filters: any) => void
  updateFiltersFromResponse: (filters: any) => void
  fetchFilters: (locationPath?: string | null) => Promise<void>
  setUnfilteredSearchResults: (results: MkSearchResponse) => void
  batchUpdateSearchCriteria: (
    location?: ISearchLocation | null,
    dates?: { startDate: Date | null; endDate: Date | null } | null,
    isFlexibleDates?: boolean,
    isBonusTimeDates?: boolean,
    flexiDatesOptions?: {
      flexiDatesString?: string
      selectedMonths?: Date[]
      numberOfNights?: number
    } | null,
    bonusTimeOptions?: {
      bonusTimeDatesString?: string
      numberOfNights?: number
    } | null,
    filterList?: MkFilterCategory[] | null,
    isDatesBufferEnabled?: boolean | null,
  ) => void
}

/**
 * Context for managing availability state and actions.
 *
 * @type {React.Context<AvailabilityContextProps>}
 *
 * @property {AvailabilityState} state - The current state of availability.
 * @property {React.Dispatch<AvailabilityAction>} dispatchAvailabilityAction - Function to dispatch actions to update the availability state.
 * @property {function(Date): void} onDateChange - Callback function to handle date changes.
 * @property {function(Date): void} onMonthChange - Callback function to handle month changes.
 * @property {function(): void} resetDates - Function to reset the dates to their initial state.
 */
export const AvailabilityContext = React.createContext<AvailabilityContextProps>({
  ...initializeAvailabilityState(),
  dispatchAvailabilityAction: () => null,
  onDateChange: () => null,
  onMonthChange: () => null,
  resetDates: () => null,
  resetLocation: () => null,
  resetFlexibleDates: () => null,
  resetBonusTimeDates: () => null,
  registerBonusTimeState: () => null,
  onLocationChange: () => null,
  onFlexibleDatesChange: () => null,
  onBonusTimeDatesChange: () => null,
  setDatesBufferEnabled: () => null,
  setSelectedFlexibleDateMonths: () => null,
  setNumberOfNights: () => null,
  formattedDates: '',
  setFlexiDatesString: () => null,
  setBonusTimeDatesString: () => null,
  setAvailableSuites: () => null,
  setSelectedSuite: () => null,
  setSelectedResort: () => null,
  setSavedSearch: () => null,
  setLoadingState: () => null,
  getFormattedDates: () => '',
  fetchSavedSearches: () => Promise.resolve([]),
  createSaveSearchPayload: () => ({ name: '' }),
  storeSavedSearch: () => Promise.resolve({}),
  deleteSavedSearch: () => Promise.resolve(false),
  searchResorts: () => Promise.resolve({ resortList: [], alertMessage: '', businessSystemCode: '', filterList: [] }),
  searchResortAvail: () => Promise.resolve({ resorts: [], alertMessage: '', businessSystemCode: '', status: '' }),
  setFilters: () => null,
  updateFiltersFromResponse: () => null,
  fetchFilters: () => Promise.resolve(),
  setUnfilteredSearchResults: () => null,
  batchUpdateSearchCriteria: () => null,
  setIsSavedSearchExecution: () => null,
})

/**
 * Hook to access the availability context.
 *
 * @returns {AvailabilityContextProps} The availability context.
 *
 * @throws {Error} If the hook is not used within an AvailabilityProvider.
 *
 * @example
 * const { state, onDateChange } = useAvailability();
 *
 * onDateChange(new Date());
 *
 * @see {@link initializeAvailabilityState}
 * @see {@link AvailabilityAction}
 * @see {@link AvailabilityActionType}
 * @see {@link availabilityReducer}
 *
 * @see {@link AvailabilityProvider}
 * @see {@link AvailabilityProviderProps}
 * @see {@link AvailabilityContextProps}
 * @see {@link AvailabilityContext}
 */
export const useAvailability = (): AvailabilityContextProps => {
  const context = React.useContext(AvailabilityContext)
  if (context === undefined) {
    throw new Error('useAvailability must be used within an AvailabilityProvider')
  }
  return context
}

export const AvailabilityProvider: React.FC<AvailabilityProviderProps> = ({ children }) => {
  const [state, dispatch] = React.useReducer(availabilityReducer, initializeAvailabilityState())
  const authHeaders = useAuthHeaders()
  const { isAuthorized } = useAuthentication()
  const { themeBrand } = useSiteContext()
  const getAuthHeaders = React.useCallback(() => authHeaders, [authHeaders])
  const getIsAuthorized = React.useCallback(() => isAuthorized, [isAuthorized])
  const availabilityService = React.useMemo(() => new AvailabilityService(state, dispatch, getAuthHeaders, getIsAuthorized), [state, dispatch, getAuthHeaders, getIsAuthorized])
  const contextValue = React.useMemo(() => availabilityService.getContextProps(), [availabilityService])

  const maxStayMonthsFallback = React.useMemo(() => {
    if (themeBrand === 'ebac') {
      return MAX_STAY_MONTHS_EBAC
    }
    return MAX_STAY_MONTHS
  }, [themeBrand])

  const serviceRef = React.useRef(availabilityService)
  serviceRef.current = availabilityService

  React.useEffect(() => {
    if (!isAuthorized || !authHeaders) {
      dispatch({
        type: AvailabilityActionType.SET_SEARCH_CONSTRAINTS,
        payload: getDefaultSearchConstraints(themeBrand),
      })
      return
    }
    fetchSearchConstraints(authHeaders as Record<string, string>)
      .then((res) => {
        if (res && typeof res.minimumNights === 'number') {
          dispatch({ type: AvailabilityActionType.SET_SEARCH_CONSTRAINTS, payload: mapSearchConstraintsResponse(res, maxStayMonthsFallback) })
        } else {
          dispatch({
            type: AvailabilityActionType.SET_SEARCH_CONSTRAINTS,
            payload: getDefaultSearchConstraints(themeBrand),
          })
        }
      })
      .catch(() => {
        dispatch({
          type: AvailabilityActionType.SET_SEARCH_CONSTRAINTS,
          payload: getDefaultSearchConstraints(themeBrand),
        })
      })
  }, [isAuthorized, authHeaders, themeBrand, dispatch])

  return <AvailabilityContext.Provider value={contextValue}>{children}</AvailabilityContext.Provider>
}
